import styled from "@emotion/styled";
import { throttle } from "lodash";
import * as React from "react";

import { TooltipContent } from "components/tooltip/TooltipContent";

export enum TooltipDirection {
  Top = "top",
  Bottom = "bottom",
  Left = "left",
  Right = "right",
}

export enum TooltipAlignment {
  Top = "top",
  Center = "center",
}

interface IProps {
  directions?: TooltipDirection[];
  align?: TooltipAlignment;
  offset?: number;
  isTooltipVisible?: boolean;
  keepContentVisibleOnHover?: boolean;
  className?: string;
  trigger: "hover" | "click" | "custom";
  children: React.ReactNode;
  content: React.ReactNode;
}

interface IState {
  isVisible: boolean;
  offset: number;
  xPosition: number;
  yPosition: number;
}

const StyledTooltip = styled.div`
  position: relative;

  &:hover {
    z-index: 99;
  }
`;

export class Tooltip extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      isVisible: false,
      offset: props.offset ? props.offset : 0,
      xPosition: 0,
      yPosition: 0,
    };

    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onContentMouseEnter = this.onContentMouseEnter.bind(this);
    this.onContentMouseLeave = this.onContentMouseLeave.bind(this);
    this.calculatePosition = this.calculatePosition.bind(this);
    this.scrollListener = throttle(() => this.onWindowScroll(), 50);
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  static getDerivedStateFromProps(props: any) {
    if (props.trigger === "custom") {
      return { isVisible: props.isTooltipVisible };
    }
    return null;
  }

  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(6133): 'prevProps' is declared but its value is never rea... Remove this comment to see the full error message
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidUpdate(prevProps: IProps, prevState: IState) {
    // Add event handlers only when tooltip is visible
    if (this.state.isVisible && !prevState.isVisible) {
      this.addEventHandlers();
    }

    // Tooltip is hidden, we don't need listen to global events anymore
    if (!this.state.isVisible && prevState.isVisible) {
      this.removeEventHandlers();
    }

    // Do recalculate tooltip position if the one is shown
    if (this.state.isVisible) {
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      const tooltipRefRect: ClientRect = this.tooltipRef.current.getBoundingClientRect();

      if (tooltipRefRect.top !== this.lastTooltipRefTop || tooltipRefRect.left !== this.lastTooltipRefLeft) {
        this.recalculatePosition();
      }
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentWillUnmount() {
    this.removeEventHandlers();
  }

  onWindowScroll(): void {
    if (this.state.isVisible) {
      this.setState({ isVisible: false });
    }
  }

  onMouseEnter(): void {
    if (this.props.trigger === "hover") {
      this.setState({ isVisible: true });
    }
  }

  onMouseLeave(): void {
    if (this.props.trigger === "hover") {
      this.setState({ isVisible: false });
    }
  }

  onContentMouseEnter(): void {
    if (this.props.trigger === "hover") {
      this.setState({ isVisible: this.props.keepContentVisibleOnHover || false });
    }
  }

  onContentMouseLeave(): void {
    if (this.props.trigger === "hover") {
      this.setState({ isVisible: false });
    }
  }

  private getAlignment(direction: TooltipDirection): TooltipAlignment {
    const supportedAlignments = {
      [TooltipDirection.Left]: [TooltipAlignment.Center, TooltipAlignment.Top],
      [TooltipDirection.Right]: [TooltipAlignment.Center],
      [TooltipDirection.Top]: [TooltipAlignment.Center],
      [TooltipDirection.Bottom]: [TooltipAlignment.Center],
    };

    if (this.props.align && supportedAlignments[direction].indexOf(this.props.align) > -1) {
      return this.props.align;
    }

    return TooltipAlignment.Center;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private addEventHandlers() {
    window.addEventListener("scroll", this.scrollListener);
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private removeEventHandlers() {
    window.removeEventListener("scroll", this.scrollListener);
  }

  private recalculatePosition(): void {
    const directionsChecked: TooltipDirection[] = [];
    let tooltipDirection = this.tooltipDirection(directionsChecked);

    while (!this.calculatePosition(tooltipDirection)) {
      directionsChecked.push(tooltipDirection);
      tooltipDirection = this.tooltipDirection(directionsChecked);
    }
  }

  private calculatePosition(direction: TooltipDirection): boolean {
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    const tooltipRefRect: ClientRect = this.tooltipRef.current.getBoundingClientRect();
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    const tooltipContentRefRect: ClientRect = this.tooltipContentRef.current.getBoundingClientRect();
    let tooltipXPosition;
    let tooltipYPosition;

    this.lastTooltipRefTop = tooltipRefRect.top;
    this.lastTooltipRefLeft = tooltipRefRect.left;
    const align = this.getAlignment(direction);

    switch (direction) {
      case TooltipDirection.Top:
        tooltipXPosition = tooltipRefRect.left + (tooltipRefRect.width / 2 - tooltipContentRefRect.width / 2);
        tooltipYPosition = tooltipRefRect.top - tooltipContentRefRect.height - this.state.offset;
        break;

      case TooltipDirection.Bottom:
        tooltipXPosition = tooltipRefRect.left + (tooltipRefRect.width / 2 - tooltipContentRefRect.width / 2);
        tooltipYPosition = tooltipRefRect.top + tooltipRefRect.height + this.state.offset;
        break;

      case TooltipDirection.Right:
        tooltipXPosition = tooltipRefRect.left + tooltipRefRect.width + this.state.offset;
        tooltipYPosition = tooltipRefRect.top + tooltipRefRect.height / 2 - tooltipContentRefRect.height / 2;
        break;
      // 131 34 220
      // 131 34 255
      case TooltipDirection.Left:
        tooltipXPosition = tooltipRefRect.left - tooltipContentRefRect.width - this.state.offset;
        switch (align) {
          case TooltipAlignment.Center:
            tooltipYPosition = tooltipRefRect.top + tooltipRefRect.height / 2 - tooltipContentRefRect.height / 2;
            break;
          case TooltipAlignment.Top:
            tooltipYPosition = tooltipRefRect.top - 4 - 10; // minus TooltipContent margin and overlap
            break;
          // no default
        }
        break;

      default:
        tooltipXPosition = 0;
        tooltipYPosition = 0;
        break;
    }

    this.setState({ xPosition: tooltipXPosition, yPosition: tooltipYPosition });

    return true;
  }

  private tooltipDirection(directionsChecked: TooltipDirection[]): TooltipDirection {
    const defaultQueue: TooltipDirection[] = [
      TooltipDirection.Top,
      TooltipDirection.Bottom,
      TooltipDirection.Left,
      TooltipDirection.Right,
    ];
    // @ts-expect-error TS(2322): Type 'TooltipDirection[] | undefined' is not assig... Remove this comment to see the full error message
    const propsQueue: TooltipDirection[] = this.props.directions;
    const propsDirection: TooltipDirection = propsQueue.filter(
      (direction: TooltipDirection) => directionsChecked.indexOf(direction) === -1
    )[0];
    const defaultDirection: TooltipDirection = defaultQueue.filter(
      (direction: TooltipDirection) => directionsChecked.indexOf(direction) === -1
    )[0];

    if (propsDirection) {
      return propsDirection;
    }
    if (defaultDirection) {
      return defaultDirection;
    }
    return TooltipDirection.Top;
  }

  tooltipRef = React.createRef<HTMLDivElement>();
  tooltipContentRef = React.createRef<HTMLDivElement>();
  // @ts-expect-error TS(2564): Property 'lastTooltipRefTop' has no initializer an... Remove this comment to see the full error message
  private lastTooltipRefTop: number;
  // @ts-expect-error TS(2564): Property 'lastTooltipRefLeft' has no initializer a... Remove this comment to see the full error message
  private lastTooltipRefLeft: number;
  private scrollListener: EventListener;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    return (
      <StyledTooltip
        ref={this.tooltipRef}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        className={this.props.className}
      >
        {this.props.children}

        <TooltipContent
          contentRef={this.tooltipContentRef}
          isVisible={(this.props.trigger === "custom" && this.props.isTooltipVisible) || this.state.isVisible}
          xPosition={this.state.xPosition}
          yPosition={this.state.yPosition}
          onContentMouseEnter={this.onContentMouseEnter}
          onContentMouseLeave={this.onContentMouseLeave}
        >
          {this.props.content}
        </TooltipContent>
      </StyledTooltip>
    );
  }
}
